/**
 * AuthShell — premium two-pane wrapper for the login & signup surfaces.
 *
 * Left: a branded gradient value panel (logo wordmark, headline, trust
 * bullets) that gives the pre-auth screens the same enterprise feel as the
 * pricing and contact dialogs. Right: the actual form, passed as children.
 *
 * Shared by Login and Signup so the two auth screens stay visually identical
 * (DRY) and consistent with the rest of the polished surfaces.
 */
import { Check, ShieldCheck } from "lucide-react";

interface Props {
  eyebrow: string;
  title: string;
  subtitle: string;
  bullets: string[];
  children: React.ReactNode;
}

export function AuthShell({ eyebrow, title, subtitle, bullets, children }: Props) {
  return (
    <div className="grid overflow-hidden rounded-2xl border border-border bg-panel shadow-2xl lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      {/* value panel */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-[#0b1220] via-[#0d1526] to-[#070a12] p-8 text-white lg:flex">
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-30 blur-2xl"
          style={{ background: "radial-gradient(closest-side, hsl(var(--accent)), transparent)" }}
          aria-hidden
        />
        <div className="relative">
          <div className="text-[16px] font-bold tracking-[0.14em]">
            ONTOLIGENT<span className="text-good">.</span>
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-white/45">
            by Redpill Software
          </div>
          <div className="mt-8 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
            {eyebrow}
          </div>
          <h2 className="mt-3 text-[1.7rem] font-semibold leading-tight tracking-tight">{title}</h2>
          <p className="mt-3 text-[13px] leading-relaxed text-white/65">{subtitle}</p>
          <ul className="mt-7 space-y-2.5">
            {bullets.map((b) => (
              <li key={b} className="flex gap-2.5 text-[12.5px] leading-snug text-white/85">
                <Check size={15} className="mt-0.5 shrink-0 text-[hsl(var(--accent))]" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative mt-8 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-[11px] text-white/55">
          <ShieldCheck size={14} className="text-[hsl(var(--accent))]" />
          Tenant-isolated by schema · SOC 2 / ISO 27001 control map · BYO-LLM keys.
        </div>
      </aside>

      {/* form */}
      <div className="p-6 sm:p-8">{children}</div>
    </div>
  );
}
